import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Bell, 
  BellRing,
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  CheckCircle2,
  Zap,
  Trash2,
  CheckCheck,
  Settings,
  Target,
  ChevronDown,
  Clock,
  ArrowDownRight,
  Smartphone,
  Send,
  Loader2,
  RefreshCw,
  X
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { getAssetName } from '../utils/assetNames';
import usePushNotifications from '../hooks/usePushNotifications';
import HowItWorks from '../components/HowItWorks';
import PWAInstallGuide from '../components/PWAInstallGuide';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [displayCount, setDisplayCount] = useState(10); // Pagination: show 10 at a time
  const [testingPush, setTestingPush] = useState(false);
  
  // Push notifications hook
  const {
    isSupported: pushSupported,
    supportDetails: pushSupportDetails,
    isSubscribed: pushSubscribed,
    permission: pushPermission,
    loading: pushLoading,
    error: pushError,
    subscribe: subscribeToPush,
    unsubscribe: unsubscribeFromPush
  } = usePushNotifications();
  
  const [notifPrefs, setNotifPrefs] = useState({
    alert_entry_hit: true,
    alert_tp1: true,
    alert_tp2: true,
    alert_tp100: true,
    alert_sl_warning: true,
    alert_sl_hit: true,
    alert_liquidation_warning: true,
    alert_liquidation: true,
    alert_dca_zone: true,
    // New alerts
    alert_tp1_retracement: false,
    alert_tp2_retracement: false,
    alert_market_open: false,
    alert_market_close: false,
    market_alerts_us: true,
    market_alerts_fr: false
  });

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/notifications/history?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPreferences = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/notifications/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifPrefs(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    fetchPreferences();
  }, [fetchNotifications, fetchPreferences]);

  const handleMarkAllRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('Toutes les notifications marquées comme lues');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleClearAll = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/notifications/clear-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications([]);
      toast.success('Historique effacé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleNotifPrefChange = async (key, value) => {
    try {
      const token = localStorage.getItem('token');
      const newPrefs = { ...notifPrefs, [key]: value };
      setNotifPrefs(newPrefs);
      
      await axios.put(`${API}/api/notifications/preferences`, newPrefs, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Préférence mise à jour');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const getNotificationIcon = (type) => {
    const t = type?.toLowerCase() || '';
    if (t.includes('intraday_24h')) return <Clock className="w-5 h-5 text-amber-400" />;
    if (t.includes('dca') || t.includes('zone_alert')) return <Target className="w-5 h-5 text-emerald-400" />;
    if (t.includes('tp1')) return <TrendingUp className="w-5 h-5 text-emerald-400" />;
    if (t.includes('tp2')) return <TrendingUp className="w-5 h-5 text-emerald-500" />;
    if (t.includes('tp100') || t.includes('tp_100')) return <TrendingUp className="w-5 h-5 text-green-400" />;
    if (t.includes('sl_warning') || t.includes('sl_alert')) return <AlertTriangle className="w-5 h-5 text-amber-400" />;
    if (t.includes('sl_hit') || t.includes('sl')) return <TrendingDown className="w-5 h-5 text-red-400" />;
    if (t.includes('liquidation')) return <AlertTriangle className="w-5 h-5 text-red-500" />;
    if (t.includes('entry') || t.includes('activated')) return <CheckCircle2 className="w-5 h-5 text-blue-400" />;
    if (t.includes('autoscan') || t.includes('scan')) return <Zap className="w-5 h-5 text-amber-400" />;
    return <Bell className="w-5 h-5 text-muted-foreground" />;
  };

  // Actions for INTRADAY 24h notifications
  const [actionLoading, setActionLoading] = useState({});
  
  const handleConvertToSwing = async (tradeId, notifId) => {
    setActionLoading(prev => ({ ...prev, [notifId]: 'convert' }));
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/trades/${tradeId}/convert-to-swing`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Trade converti en SWING');
      // Mark notification as read
      await axios.patch(`${API}/api/notifications/${notifId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (error) {
      toast.error('Erreur lors de la conversion');
    } finally {
      setActionLoading(prev => ({ ...prev, [notifId]: null }));
    }
  };

  const handleDismiss24hWarning = async (tradeId, notifId) => {
    // Handle test notifications (no real trade)
    if (tradeId === 'test-trade-id' || !tradeId) {
      // Just mark the notification as read
      try {
        const token = localStorage.getItem('token');
        await axios.patch(`${API}/api/notifications/${notifId}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Notification marquée comme lue');
        fetchNotifications();
      } catch (error) {
        toast.error('Erreur');
      }
      return;
    }
    
    setActionLoading(prev => ({ ...prev, [notifId]: 'dismiss' }));
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/trades/${tradeId}/dismiss-24h-warning`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Mark notification as read
      await axios.patch(`${API}/api/notifications/${notifId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Trade laissé ouvert');
      fetchNotifications();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setActionLoading(prev => ({ ...prev, [notifId]: null }));
    }
  };

  const handleTest24hNotification = async () => {
    setTestingPush(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/notifications/test-intraday-24h`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Notification 24h envoyée !');
      fetchNotifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setTestingPush(false);
    }
  };

  // Helper to get full asset name from symbol
  const getFullAssetName = (symbol) => {
    if (!symbol) return '';
    // Clean the symbol for lookup
    const cleanSymbol = symbol.replace(/^[A-Z]+:/, '').replace(/\/.*$/, '').replace(/-.*$/, '');
    const fullName = getAssetName(cleanSymbol);
    // If we got a different name than the symbol, use it
    if (fullName && fullName !== cleanSymbol && fullName !== symbol) {
      return fullName;
    }
    return symbol;
  };

  const getNotificationTitle = (type, data) => {
    const t = type?.toLowerCase() || '';
    const symbol = data?.symbol || '';
    const fullName = getFullAssetName(symbol);
    const prefix = fullName ? `${fullName} - ` : '';
    
    if (t.includes('dca') || t.includes('zone_alert')) {
      const zoneType = data?.zone_type === 'strong_buy' ? '⭐ ZONE OTE' : 'Zone d\'achat';
      return `${prefix}${zoneType} atteinte!`;
    }
    if (t.includes('analysis_complete')) return `${prefix}Analyse IA prête`;
    if (t.includes('analysis_failed')) return `${prefix}Analyse IA échouée`;
    if (t.includes('tp1_retracement')) return `${prefix}Retour sous TP1`;
    if (t.includes('tp2_retracement')) return `${prefix}Retour sous TP2`;
    if (t.includes('tp1')) return `${prefix}TP1 atteint (25%)`;
    if (t.includes('tp2')) return `${prefix}TP2 atteint (50%)`;
    if (t.includes('tp100') || t.includes('tp_100')) return `${prefix}TP100% atteint!`;
    if (t.includes('sl_warning') || t.includes('sl_alert')) return `${prefix}Alerte Stop Loss`;
    if (t.includes('sl_hit') || (t.includes('sl') && !t.includes('warning'))) return `${prefix}Stop Loss touché`;
    if (t.includes('liquidation_warning')) return `${prefix}Alerte Liquidation`;
    if (t.includes('liquidation')) return `${prefix}Position liquidée`;
    if (t.includes('entry') || t.includes('activated')) return `${prefix}Trade activé`;
    if (t.includes('24h_warning') || t.includes('intraday_24h')) return `${prefix}INTRADAY > 24h`;
    if (t.includes('setup_alert')) return `${prefix}Setup détecté`;
    if (t.includes('autoscan') || t.includes('scan')) return `${prefix}Opportunité détectée`;
    if (t.includes('eco_result')) return `${data?.event_name || 'Résultat économique'} publié`;
    if (t.includes('market_open')) return 'Ouverture des marchés';
    if (t.includes('market_close')) return 'Clôture des marchés';
    if (t.includes('gift')) return `Cadeau ${data?.tier ? data.tier.toUpperCase() : ''}`.trim();
    return fullName ? `${fullName} - Notification` : 'Notification';
  };

  const buildNotificationUrl = (notif) => {
    const t = notif.type?.toLowerCase() || '';
    const data = notif.data || {};
    const tradeId = data.trade_id;
    const symbol = data.symbol;
    if (notif.url && typeof notif.url === 'string' && notif.url.startsWith('/')) return notif.url;
    if (data.url && typeof data.url === 'string' && data.url.startsWith('/')) return data.url;
    if (
      t.includes('tp1') || t.includes('tp2') || t.includes('tp100') || t.includes('tp_100') ||
      t.includes('sl') || t.includes('liquidation') || t.includes('entry') || t.includes('activated') ||
      t.includes('retracement') || t.includes('24h_warning')
    ) return tradeId ? `/history?trade_id=${encodeURIComponent(tradeId)}` : '/history';
    if (t.includes('setup_alert') || t.includes('autoscan') || t.includes('scan')) {
      return symbol ? `/analysis?symbol=${encodeURIComponent(symbol)}` : '/analysis';
    }
    if (t.includes('analysis')) return '/analysis';
    if (t.includes('dca')) return '/dca';
    if (t.includes('eco')) return '/news-eco';
    if (t.includes('market_open') || t.includes('market_close')) return '/history';
    if (t.includes('gift')) return '/subscribe';
    return null;
  };

  const handleNotificationClick = async (notif) => {
    const url = buildNotificationUrl(notif);
    if (!url) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/notifications/${notif.id || notif._id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (_e) { /* ignore */ }
    window.location.href = url;
  };

  const getNotificationMessage = (notif) => {
    const data = notif.data || {};
    const t = notif.type?.toLowerCase() || '';
    
    // DCA Zone Alert message
    if (t.includes('dca') || t.includes('zone_alert')) {
      const price = data.price?.toLocaleString() || '?';
      const multiplier = data.multiplier || 1;
      const reason = data.zone_reason || 'Zone technique';
      return `Prix: $${price} • Multiplicateur: x${multiplier} • ${reason}`;
    }
    
    if (data.pnl_percent !== undefined) {
      return `PnL: ${data.pnl_percent > 0 ? '+' : ''}${data.pnl_percent.toFixed(2)}%`;
    }
    if (data.sl_progress !== undefined) {
      return `Progression SL: ${data.sl_progress.toFixed(0)}%`;
    }
    if (data.direction && data.entry_price) {
      return `${data.direction} @ ${data.entry_price}`;
    }
    return data.symbol ? getFullAssetName(data.symbol) : notif.message || '';
  };

  const formatTime = (dateStr) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: fr });
    } catch {
      return '';
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  // Handle push notification activation
  const handleActivatePush = async () => {
    const success = await subscribeToPush(false);
    if (success) {
      toast.success('Notifications Push activées !');
    } else if (pushPermission === 'denied') {
      toast.error('Notifications bloquées. Veuillez autoriser les notifications dans les paramètres de votre navigateur.');
    }
  };

  // Handle push notification deactivation
  const handleDeactivatePush = async () => {
    const success = await unsubscribeFromPush();
    if (success) {
      toast.success('Notifications Push désactivées');
    }
  };

  // Test push notification
  const handleTestPush = async () => {
    setTestingPush(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/notifications/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Notification de test envoyée !');
    } catch (error) {
      console.error('Test push error:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors de l\'envoi';
      toast.error(errorMsg);
    } finally {
      setTestingPush(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Bell className="w-6 h-6 text-primary" />
              Notifications
            </h1>
            <HowItWorks pageKey="notifications" />
          </div>
          <p className="text-muted-foreground text-sm mt-1">
            Historique et paramètres de vos alertes
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant={showSettings ? "default" : "outline"}
            size="sm"
            onClick={() => setShowSettings(!showSettings)}
          >
            <Settings className="w-4 h-4 mr-1" />
            Paramètres
          </Button>
        </div>
      </div>

      {/* Push Notifications Activation Panel */}
      <div className="glass rounded-2xl p-6 border border-border" data-testid="push-notifications-panel">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <BellRing className="w-5 h-5 text-primary" />
              Notifications Push Mobile
            </h2>
            <p className="text-muted-foreground text-sm mt-1">
              Recevez des alertes en temps réel sur votre téléphone, même quand l&apos;application est fermée.
            </p>
            
            {!pushSupported ? (
              <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <p className="text-amber-400 text-sm font-medium mb-2">
                  ⚠️ Notifications Push non disponibles sur ce navigateur
                </p>
                {pushSupportDetails && (
                  <div className="text-amber-300/80 text-xs space-y-1 mt-2">
                    {!pushSupportDetails.isSecureContext && (
                      <p>• <strong>HTTPS requis</strong> — les push notifications ne fonctionnent qu&apos;en HTTPS</p>
                    )}
                    {!pushSupportDetails.hasServiceWorker && (
                      <p>• <strong>Service Worker indisponible</strong> — mode incognito/privé bloque cette fonctionnalité</p>
                    )}
                    {!pushSupportDetails.hasPushManager && (
                      <p>• <strong>PushManager indisponible</strong> — navigateur non compatible ou trop ancien</p>
                    )}
                    {!pushSupportDetails.hasNotification && (
                      <p>• <strong>API Notification indisponible</strong> — navigateur non compatible</p>
                    )}
                    {/iPhone|iPad|iPod/.test(pushSupportDetails.userAgent) && !pushSupportDetails.isStandalone && (
                      <div className="mt-3 p-2 rounded bg-amber-500/10 border border-amber-500/20">
                        <p className="font-medium text-amber-300">📱 iOS détecté :</p>
                        <p className="mt-1">Apple exige que l&apos;app soit installée en <strong>PWA</strong> pour recevoir les notifications.</p>
                        <p className="mt-1"><strong>Comment faire :</strong></p>
                        <ol className="list-decimal ml-4 mt-1 space-y-0.5">
                          <li>Ouvre l&apos;app dans <strong>Safari</strong> (obligatoire, pas Chrome)</li>
                          <li>Touche le bouton <strong>Partager</strong> (carré avec flèche vers le haut)</li>
                          <li>Choisis <strong>&quot;Sur l&apos;écran d&apos;accueil&quot;</strong></li>
                          <li>Lance l&apos;app depuis l&apos;icône (pas depuis le navigateur)</li>
                          <li>Reviens sur cette page → les notifications seront disponibles</li>
                        </ol>
                      </div>
                    )}
                    <details className="mt-3">
                      <summary className="cursor-pointer text-amber-300/60 hover:text-amber-300">Détails techniques</summary>
                      <div className="mt-2 p-2 rounded bg-black/30 font-mono text-[10px] text-amber-200/70 space-y-0.5">
                        <p>Service Worker: {String(pushSupportDetails.hasServiceWorker)}</p>
                        <p>PushManager: {String(pushSupportDetails.hasPushManager)}</p>
                        <p>Notification API: {String(pushSupportDetails.hasNotification)}</p>
                        <p>HTTPS (secure context): {String(pushSupportDetails.isSecureContext)}</p>
                        <p>PWA installée (standalone): {String(pushSupportDetails.isStandalone)}</p>
                      </div>
                    </details>
                  </div>
                )}
              </div>
            ) : pushPermission === 'denied' ? (
              <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <p className="text-red-400 text-sm">
                  🚫 Les notifications sont bloquées. Veuillez les autoriser dans les paramètres de votre navigateur.
                </p>
              </div>
            ) : pushSubscribed ? (
              <div className="mt-4 space-y-3">
                <div className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="text-sm font-medium">Notifications Push activées</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleTestPush}
                    disabled={testingPush}
                    data-testid="test-push-btn"
                  >
                    {testingPush ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-1" />
                    )}
                    Tester
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleTest24hNotification}
                    disabled={testingPush}
                    className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                    data-testid="test-24h-btn"
                  >
                    {testingPush ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Clock className="w-4 h-4 mr-1" />
                    )}
                    Test Alerte 24h
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDeactivatePush}
                    disabled={pushLoading}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    data-testid="deactivate-push-btn"
                  >
                    Désactiver
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-4">
                <Button
                  onClick={handleActivatePush}
                  disabled={pushLoading}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="activate-push-btn"
                >
                  {pushLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <BellRing className="w-4 h-4 mr-2" />
                  )}
                  Activer les notifications Push
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  💡 Une demande d&apos;autorisation apparaîtra. Cliquez sur &quot;Autoriser&quot; pour recevoir les alertes.
                </p>
              </div>
            )}
            
            {pushError && (
              <p className="text-red-400 text-sm mt-2">{pushError}</p>
            )}
          </div>
        </div>
      </div>

      {/* PWA Installation Guide — critical for push notifications */}
      <PWAInstallGuide />

      {/* Settings Panel */}
      {showSettings && (
        <div className="glass rounded-2xl p-6 border border-border">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-primary" />
            Paramètres des alertes
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between py-2 border-b border-border">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-blue-400" />
                <span className="text-foreground text-sm">Entrée touchée</span>
              </div>
              <Switch checked={notifPrefs.alert_entry_hit} onCheckedChange={(v) => handleNotifPrefChange('alert_entry_hit', v)} />
            </div>
            
            <div className="flex items-center justify-between py-2 border-b border-border">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
                <span className="text-foreground text-sm">TP1 atteint (25%)</span>
              </div>
              <Switch checked={notifPrefs.alert_tp1} onCheckedChange={(v) => handleNotifPrefChange('alert_tp1', v)} />
            </div>
            
            <div className="flex items-center justify-between py-2 border-b border-border">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
                <span className="text-foreground text-sm">TP2 atteint (50%)</span>
              </div>
              <Switch checked={notifPrefs.alert_tp2} onCheckedChange={(v) => handleNotifPrefChange('alert_tp2', v)} />
            </div>
            
            <div className="flex items-center justify-between py-2 border-b border-border">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-green-400" />
                <span className="text-foreground text-sm">TP100% atteint</span>
              </div>
              <Switch checked={notifPrefs.alert_tp100} onCheckedChange={(v) => handleNotifPrefChange('alert_tp100', v)} />
            </div>
            
            <div className="flex items-center justify-between py-2 border-b border-border">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                <span className="text-foreground text-sm">Alerte Stop Loss (50%)</span>
              </div>
              <Switch checked={notifPrefs.alert_sl_warning} onCheckedChange={(v) => handleNotifPrefChange('alert_sl_warning', v)} />
            </div>
            
            <div className="flex items-center justify-between py-2 border-b border-border">
              <div className="flex items-center gap-3">
                <TrendingDown className="w-5 h-5 text-red-400" />
                <span className="text-foreground text-sm">Stop Loss touché</span>
              </div>
              <Switch checked={notifPrefs.alert_sl_hit} onCheckedChange={(v) => handleNotifPrefChange('alert_sl_hit', v)} />
            </div>
            
            {/* DCA Zone Alert - New */}
            <div className="flex items-center justify-between py-2 border-b border-border">
              <div className="flex items-center gap-3">
                <Target className="w-5 h-5 text-emerald-400" />
                <div>
                  <span className="text-foreground text-sm">Zone DCA atteinte</span>
                  <p className="text-xs text-muted-foreground">Alerte quand le prix entre dans une zone d&apos;achat OTE</p>
                </div>
              </div>
              <Switch checked={notifPrefs.alert_dca_zone} onCheckedChange={(v) => handleNotifPrefChange('alert_dca_zone', v)} />
            </div>
            
            {/* TP Retracement Alerts */}
            <div className="mt-4 mb-2">
              <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <ArrowDownRight className="w-4 h-4" />
                Alertes de Retracement
              </h3>
              <p className="text-xs text-muted-foreground mt-1">Soyez alerté si le prix repasse sous un niveau TP déjà atteint</p>
            </div>
            
            <div className="flex items-center justify-between py-2 border-b border-border">
              <div className="flex items-center gap-3">
                <ArrowDownRight className="w-5 h-5 text-orange-400" />
                <div>
                  <span className="text-foreground text-sm">Retracement sous TP1</span>
                  <p className="text-xs text-muted-foreground">Alerte si le prix repasse sous TP1 après l&apos;avoir touché</p>
                </div>
              </div>
              <Switch checked={notifPrefs.alert_tp1_retracement} onCheckedChange={(v) => handleNotifPrefChange('alert_tp1_retracement', v)} />
            </div>
            
            <div className="flex items-center justify-between py-2 border-b border-border">
              <div className="flex items-center gap-3">
                <ArrowDownRight className="w-5 h-5 text-orange-500" />
                <div>
                  <span className="text-foreground text-sm">Retracement sous TP2</span>
                  <p className="text-xs text-muted-foreground">Alerte si le prix repasse sous TP2 après l&apos;avoir touché</p>
                </div>
              </div>
              <Switch checked={notifPrefs.alert_tp2_retracement} onCheckedChange={(v) => handleNotifPrefChange('alert_tp2_retracement', v)} />
            </div>
            
            {/* Market Hours Alerts */}
            <div className="mt-4 mb-2">
              <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Alertes Horaires de Marché
              </h3>
              <p className="text-xs text-muted-foreground mt-1">Notifications à l&apos;ouverture et fermeture des marchés</p>
            </div>
            
            <div className="flex items-center justify-between py-2 border-b border-border">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-blue-400" />
                <div>
                  <span className="text-foreground text-sm">Ouverture de marché</span>
                  <p className="text-xs text-muted-foreground">Alerte quand le marché ouvre</p>
                </div>
              </div>
              <Switch checked={notifPrefs.alert_market_open} onCheckedChange={(v) => handleNotifPrefChange('alert_market_open', v)} />
            </div>
            
            <div className="flex items-center justify-between py-2 border-b border-border">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-purple-400" />
                <div>
                  <span className="text-foreground text-sm">Fermeture de marché</span>
                  <p className="text-xs text-muted-foreground">Alerte quand le marché ferme (rappel trades Intraday)</p>
                </div>
              </div>
              <Switch checked={notifPrefs.alert_market_close} onCheckedChange={(v) => handleNotifPrefChange('alert_market_close', v)} />
            </div>
            
            {/* Market Selection - Only shown if market alerts are enabled */}
            {(notifPrefs.alert_market_open || notifPrefs.alert_market_close) && (
              <div className="ml-8 space-y-2 pb-2 border-b border-border">
                <div className="flex items-center justify-between py-1">
                  <span className="text-sm text-muted-foreground">🇺🇸 Marché US (NYSE/NASDAQ)</span>
                  <Switch checked={notifPrefs.market_alerts_us} onCheckedChange={(v) => handleNotifPrefChange('market_alerts_us', v)} />
                </div>
                <div className="flex items-center justify-between py-1">
                  <span className="text-sm text-muted-foreground">🇫🇷 Euronext Paris</span>
                  <Switch checked={notifPrefs.market_alerts_fr} onCheckedChange={(v) => handleNotifPrefChange('market_alerts_fr', v)} />
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <span className="text-foreground text-sm">Alerte Liquidation</span>
              </div>
              <Switch checked={notifPrefs.alert_liquidation_warning} onCheckedChange={(v) => handleNotifPrefChange('alert_liquidation_warning', v)} />
            </div>
          </div>
        </div>
      )}

      {/* Notifications List */}
      <div className="glass rounded-2xl border border-border overflow-hidden">
        {/* List Header */}
        {notifications.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-white/5">
            <span className="text-sm text-muted-foreground">
              {unreadCount > 0 ? `${unreadCount} non lue(s)` : 'Toutes lues'} • {notifications.length} total
            </span>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" onClick={handleMarkAllRead} className="text-xs">
                  <CheckCheck className="w-4 h-4 mr-1" />
                  Tout marquer lu
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={handleClearAll} className="text-xs text-red-400 hover:text-red-300">
                <Trash2 className="w-4 h-4 mr-1" />
                Effacer
              </Button>
            </div>
          </div>
        )}
        
        {notifications.length > 0 ? (
          <>
            <div className="divide-y divide-border">
              {notifications.slice(0, displayCount).map((notif, idx) => {
                const isIntraday24h = notif.type?.toLowerCase().includes('intraday_24h');
                const tradeId = notif.data?.trade_id;
                const notifId = notif.id || notif._id;
                
                return (
                  <div
                    key={notifId || idx}
                    className={`flex items-start gap-4 p-4 transition-colors cursor-pointer ${notif.read ? 'bg-transparent' : 'bg-primary/5'} hover:bg-muted/50`}
                    onClick={(e) => {
                      // Don't hijack clicks on the inline action buttons (24h warning)
                      if (e.target.closest('button')) return;
                      handleNotificationClick(notif);
                    }}
                    data-testid={`notification-item-${notifId || idx}`}
                  >
                    <div className="flex-shrink-0 mt-1 w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                      {getNotificationIcon(notif.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm ${notif.read ? 'text-muted-foreground' : 'text-foreground font-medium'}`}>
                          {getNotificationTitle(notif.type, notif.data)}
                        </p>
                        {!notif.read && (
                          <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-2"></span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {getNotificationMessage(notif)}
                      </p>
                      
                      {/* Action buttons for INTRADAY 24h warnings */}
                      {isIntraday24h && tradeId && !notif.read && (
                        <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                          <p className="text-xs text-amber-400 mb-3">
                            Attention, 24h se sont écoulées. Que souhaitez-vous faire ?
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs border-red-500/50 text-red-400 hover:bg-red-500/10"
                              onClick={() => window.location.href = `/history?trade_id=${tradeId}`}
                              data-testid="close-trade-btn"
                            >
                              <X className="w-3 h-3 mr-1" />
                              Clôturer
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                              onClick={() => handleConvertToSwing(tradeId, notifId)}
                              disabled={actionLoading[notifId] === 'convert'}
                              data-testid="convert-swing-btn"
                            >
                              {actionLoading[notifId] === 'convert' ? (
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              ) : (
                                <RefreshCw className="w-3 h-3 mr-1" />
                              )}
                              Convertir en Swing
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                              onClick={() => handleDismiss24hWarning(tradeId, notifId)}
                              disabled={actionLoading[notifId] === 'dismiss'}
                              data-testid="leave-open-btn"
                            >
                              {actionLoading[notifId] === 'dismiss' ? (
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              ) : (
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                              )}
                              Laisser ouvert
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        {formatTime(notif.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Load More Button - Pagination */}
            {notifications.length > displayCount && (
              <div className="p-4 border-t border-border">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setDisplayCount(prev => prev + 10)}
                >
                  <ChevronDown className="w-4 h-4 mr-2" />
                  Voir plus ({notifications.length - displayCount} restantes)
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="py-16 text-center">
            <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground text-lg font-medium">Aucune notification</p>
            <p className="text-muted-foreground text-sm mt-1">
              Vos alertes de trades apparaîtront ici
            </p>
          </div>
        )}
      </div>
      
    </div>
  );
}
